/**
 * Template Execution Service V2
 * 
 * A simplified, focused approach to template execution that:
 * 1. Only processes the fields specified in the template
 * 2. Uses field-specific system prompts
 * 3. Provides clear progress tracking and error handling
 * 
 * This file serves as the execution engine for the V2 template system.
 */
import { 
  prepareFilteredProductData,
  processTemplateResult 
} from './templateProcessingServiceV2';
import { processTemplateWithOpenAI } from './openaiServiceV2';
import { trackTemplateUsage } from './templateUsageService';
import { getTemplateById } from './templateService';
import { getUserModelSettings } from './aiModelService';
import { deductCreditsForAIModel } from './creditService';
import { refundCredits } from './optimizedCreditService';

/**
 * Helper function to get the current value for a variant field, considering localEdits
 * @param {string} productId - The product ID
 * @param {string} variantId - The variant ID
 * @param {string} field - The field name
 * @param {any} originalValue - The original value from the product data
 * @returns {any} The current value (from localEdits if available, otherwise original)
 */
const getCurrentVariantValue = (productId, variantId, field, originalValue) => {
  if (typeof window === 'undefined' || !window.localEdits) {
    return originalValue;
  }
  
  try {
    // Check if there's an updated value in localEdits
    if (
      window.localEdits[productId] && 
      window.localEdits[productId].variant_updates && 
      window.localEdits[productId].variant_updates[variantId] && 
      window.localEdits[productId].variant_updates[variantId][field] !== undefined
    ) {
      const updatedValue = window.localEdits[productId].variant_updates[variantId][field];
      return updatedValue !== null ? updatedValue : originalValue;
    }
  } catch (error) {
    console.error('Error getting current variant value:', error);
  }
  
  return originalValue;
};

/**
 * Helper function to get the current value for any product field, considering localEdits
 * @param {Object} productData - The product data object (contains both filtered and full product)
 * @param {string} field - The field name to get
 * @returns {any} The current value (from localEdits/filtered data if available, otherwise original)
 */
const getCurrentProductValue = (productData, field) => {
  const fullProduct = productData.fullProduct || productData;
  
  // First priority: Check if the field is already in the filtered productData (which includes local edits)
  if (productData[field] !== undefined && productData !== fullProduct) {
    console.log('%c[Using Filtered Data Value]', 'color: #00cc66; font-weight: bold', {
      field,
      value: productData[field],
      source: 'filtered_data'
    });
    return productData[field];
  }
  
  // Second priority: Check for local edits in the UI
  if (typeof window !== 'undefined' && window.localEdits && fullProduct?.id) {
    const productId = fullProduct.id.toString();
    if (window.localEdits[productId] && window.localEdits[productId][field] !== undefined) {
      const localValue = window.localEdits[productId][field];
      console.log('%c[Using Local Edit Value]', 'color: #ff6600; font-weight: bold', {
        field,
        originalValue: fullProduct[field],
        currentValue: localValue,
        productId,
        source: 'local_edits'
      });
      return localValue;
    }
  }
  
  // Third priority: Use original value from fullProduct
  const originalValue = fullProduct[field];
  console.log('%c[Using Original Value]', 'color: #666666; font-weight: bold', {
    field,
    value: originalValue,
    source: 'original_data'
  });
  return originalValue;
};

/**
 * Process a tool template without using AI
 * @param {Object} template - The template to process
 * @param {Object} productData - The product data to process
 * @returns {Object} The processing result
 */
const processToolTemplate = async (template, productData) => {
  console.log('Processing tool template:', template.name);
  
  // For tool templates, we'll execute the appropriate tool function directly
  // No need to call AI API for tool templates
  // Parse the prompt to determine which tool to execute
  // Format: tool:toolName|parameters
  let result = {};
  
  if (!template.prompt.startsWith('tool:')) {
    return { error: 'Invalid tool template format. Prompt should start with "tool:"' };
  }
  
  // Parse the tool name and parameters
  console.log('Full template prompt:', template.prompt);
  const promptParts = template.prompt.substring(5).split('|');
  console.log('Prompt parts after splitting:', promptParts);
  
  const toolPart = promptParts[0];
  // Join all remaining parts with | to preserve any | characters in the parameters
  const paramsPart = promptParts.slice(1).join('|');
  
  const toolName = toolPart.trim();
  const params = paramsPart ? paramsPart.trim() : '';
  
  console.log('Parsed tool parts:', { toolPart, paramsPart, toolName, params });
  
  console.log('Executing tool template:', { toolName, params });
  
  try {
    // Get the full product data for all tool templates
    const fullProduct = productData.fullProduct || productData;
    
    // Execute the appropriate tool based on the tool name
    switch (toolName) {
      case 'image_alt_text':
        // Generate image alt text from product title
        const altTextSuffix = params || ' - product image';
        
        // Use the helper function to get the current product title
        const productTitle = getCurrentProductValue(productData, 'title') || productData?.id?.split('/').pop() || 'Product';
        
        result.alt_text = productTitle + altTextSuffix;
        console.log('Tool executed: Generated image alt text', {
          productTitle,
          suffix: altTextSuffix,
          result: result.alt_text
        });
        break;
        
      case 'vendor_name':
        // Set vendor name
        result.vendor = params;
        console.log('Tool executed: Set vendor name', {
          vendorName: params,
          result: result.vendor
        });
        break;
      
      case 'generate_skus':
        // Generate SKUs for variants
        const skuParams = {};
        console.log('Raw params string for SKU generation:', params);
        
        if (params) {
          const paramParts = params.split('|');
          console.log('Param parts after splitting:', paramParts);
          
          paramParts.forEach(param => {
            console.log('Processing param part:', param);
            const [key, value] = param.split('=');
            console.log('Key-value pair:', { key, value });
            
            if (key && value !== undefined) {
              skuParams[key.trim()] = value.trim();
              console.log(`Added parameter ${key.trim()} = ${value.trim()}`);
            }
          });
        }
        
        console.log('Final parsed SKU parameters:', skuParams);
        
        // Get prefix and suffix from parameters
        const prefix = skuParams.prefix || '';
        const suffix = skuParams.suffix || '';
        
        console.log('SKU parameters:', { params, skuParams, prefix, suffix });
        
        // Get the current title from workflow updates, local edits, or original product
        console.log('🔍 Getting current product title for SKU generation...');
        let currentTitle = '';
        
        // 1. First check latestUpdates from previous templates in the workflow
        if (productData.latestUpdates && productData.latestUpdates.title) {
          currentTitle = productData.latestUpdates.title;
          console.log('📋 Using title from workflow updates:', { currentTitle, source: 'latestUpdates' });
        }
        
        // 2. Then check local edits from the UI
        if (!currentTitle && typeof window !== 'undefined' && window.localEdits && fullProduct.id) {
          const productId = fullProduct.id.toString();
          if (window.localEdits[productId] && window.localEdits[productId].title !== undefined) {
            currentTitle = window.localEdits[productId].title;
            console.log('📋 Using title from local edits:', { currentTitle, productId, source: 'localEdits' });
          }
        }
        
        // 3. Fallback to original title if no updates available
        if (!currentTitle) {
          currentTitle = fullProduct.title || '';
          console.log('📋 Using original title:', { currentTitle, source: 'original' });
        }
        
        // Make sure we have a valid product title
        if (!currentTitle) {
          return { error: 'Product title is required to generate SKUs' };
        }
        
        // Helper function to slugify text for SKUs (same as in ProductTools.js)
        const slugifyForSku = (text) => {
          if (!text) return '';
          
          return text
            .toLowerCase()
            .replace(/[^\w\s-]/g, '') // Remove special characters
            .replace(/\s+/g, '-')     // Replace spaces with hyphens
            .replace(/-+/g, '-')      // Replace multiple hyphens with single hyphen
            .replace(/^-+|-+$/g, ''); // Trim hyphens from start and end
        };
        
        // Get first 2 words of the title and slugify (same as in ProductTools.js)
        const getFirstNWords = (text, n) => {
          if (!text) return '';
          const words = text.split(/\s+/);
          return words.slice(0, n).join(' ');
        };
        
        const shortTitle = getFirstNWords(currentTitle, 2);
        const slugifiedTitle = slugifyForSku(shortTitle);
        
        // Create SKUs for each variant
        result.variants = {};
        
        // Get variants from the full product data
        const variants = fullProduct.variants || [];
        
        // Debug log to see the variant structure
        console.log('First variant data:', variants.length > 0 ? JSON.stringify(variants[0], null, 2) : 'No variants');
        
        if (variants.length > 0) {
          // Get productId for local edits lookup
          const productId = fullProduct.id ? fullProduct.id.toString() : null;
          
          variants.forEach(variant => {
            // Debug log for each variant
            console.log('Processing variant:', {
              id: variant.id,
              title: variant.title,
              option1: variant.option1,
              option2: variant.option2,
              option3: variant.option3,
              options: variant.options
            });
            
            // Helper function to get translated option value
            const getTranslatedOptionValue = (optionIndex, originalValue) => {
              // First try to find the translated value by looking up the original value in the option values array
              if (typeof window !== 'undefined' && window.localEdits && productId && window.localEdits[productId]) {
                const optionValuesField = `option${optionIndex}_values`;
                if (window.localEdits[productId][optionValuesField]) {
                  // Get the translated option values array
                  const translatedValues = window.localEdits[productId][optionValuesField];
                  
                  // Convert string to array if needed
                  let translatedValuesArray = translatedValues;
                  if (typeof translatedValues === 'string') {
                    translatedValuesArray = translatedValues.split(',').map(v => v.trim());
                  }
                  
                  // Try to find the original value in the product's options and map to translated
                  if (Array.isArray(translatedValuesArray)) {
                    // Find the index of the original value in the original product options
                    if (fullProduct && fullProduct.options) {
                      const option = fullProduct.options.find(opt => opt.position === optionIndex);
                      if (option && option.values) {
                        const originalIndex = option.values.findIndex(val => val === originalValue);
                        
                        if (originalIndex !== -1 && translatedValuesArray[originalIndex]) {
                          return translatedValuesArray[originalIndex];
                        }
                      }
                    }
                  }
                }
              }
              
              // Fall back to original value
              return originalValue;
            };

            // Get variant options - try different approaches to access options
            const optionValues = [];
            
            // Try direct option properties first with translation
            if (variant.option1) {
              const translatedValue = getTranslatedOptionValue(1, variant.option1);
              optionValues.push(slugifyForSku(translatedValue));
            }
            if (variant.option2) {
              const translatedValue = getTranslatedOptionValue(2, variant.option2);
              optionValues.push(slugifyForSku(translatedValue));
            }
            if (variant.option3) {
              const translatedValue = getTranslatedOptionValue(3, variant.option3);
              optionValues.push(slugifyForSku(translatedValue));
            }
            
            // If no options found, try the optionValues array if available
            if (optionValues.length === 0 && variant.optionValues && Array.isArray(variant.optionValues)) {
              variant.optionValues.forEach((originalValue, index) => {
                if (originalValue) {
                  const translatedValue = getTranslatedOptionValue(index + 1, originalValue);
                  optionValues.push(slugifyForSku(translatedValue));
                }
              });
            }
            
            // If no options found, try the options array if available
            if (optionValues.length === 0 && variant.options && Array.isArray(variant.options)) {
              variant.options.forEach((option, index) => {
                if (option) {
                  const translatedValue = getTranslatedOptionValue(index + 1, option);
                  optionValues.push(slugifyForSku(translatedValue));
                }
              });
            }
            
            // If still no options, try to extract from variant title
            if (optionValues.length === 0 && variant.title) {
              const titleParts = variant.title.split(' / ');
              if (titleParts.length > 1) { 
                // Skip the first part (product title) and only use the actual option values
                titleParts.slice(1).forEach((part, index) => {
                  const translatedValue = getTranslatedOptionValue(index + 1, part);
                  optionValues.push(slugifyForSku(translatedValue));
                });
              }
            }
            
            const optionValuesString = optionValues.join('-');
            console.log('Option values for variant:', {
              variantId: variant.id,
              optionValues,
              optionValuesString
            });
            
            // Build the SKU with optional prefix and suffix (same pattern as ProductTools.js)
            let newSku = '';
            
            // Add prefix if exists
            if (prefix) {
              newSku += prefix;
            }
            
            // Add slugified title if exists
            if (slugifiedTitle) {
              if (newSku && !newSku.endsWith('-')) newSku += '-';
              newSku += slugifiedTitle;
            }
            
            // Add option values if exist
            if (optionValuesString) {
              if (newSku && !newSku.endsWith('-')) newSku += '-';
              newSku += optionValuesString;
            }
            
            // Add suffix if exists
            if (suffix) {
              console.log('Adding suffix to SKU:', { suffix, currentSku: newSku });
              if (newSku && !newSku.endsWith('-')) newSku += '-';
              newSku += suffix;
              console.log('SKU after adding suffix:', newSku);
            } else {
              console.log('No suffix to add to SKU');
            }
            
            // Ensure SKU doesn't exceed 255 characters
            if (newSku.length > 255) {
              newSku = newSku.substring(0, 255);
            }
            
            result.variants[variant.id] = { sku: newSku };
          });
        }
        
        console.log('Tool executed: Generated SKUs', {
          prefix,
          suffix,
          productTitle: currentTitle,
          originalTitle: fullProduct.title,
          variantCount: Object.keys(result.variants || {}).length
        });
        break;
        
      case 'generate_handles':
        // Get the current title from workflow updates, local edits, or original product
        let currentTitleForHandle = '';
        
        // 1. First check latestUpdates from previous templates in the workflow
        if (productData.latestUpdates && productData.latestUpdates.title) {
          currentTitleForHandle = productData.latestUpdates.title;
          console.log('📋 Using title from workflow updates for handle:', { currentTitleForHandle, source: 'latestUpdates' });
        }
        
        // 2. Then check local edits from the UI
        if (!currentTitleForHandle && typeof window !== 'undefined' && window.localEdits && fullProduct.id) {
          const productId = fullProduct.id.toString();
          if (window.localEdits[productId] && window.localEdits[productId].title !== undefined) {
            currentTitleForHandle = window.localEdits[productId].title;
            console.log('📋 Using title from local edits for handle:', { currentTitleForHandle, productId, source: 'localEdits' });
          }
        }
        
        // 3. Fallback to original title if no updates available
        if (!currentTitleForHandle) {
          currentTitleForHandle = fullProduct.title || '';
          console.log('📋 Using original title for handle:', { currentTitleForHandle, source: 'original' });
        }
        
        // Make sure we have a valid product title
        if (!currentTitleForHandle) {
          return { error: 'Product title is required to generate handle' };
        }
        
        // Generate handle from title
        const handle = currentTitleForHandle
          .toLowerCase()
          .replace(/[^\w\s-]/g, '')
          .replace(/[\s_-]+/g, '-')
          .replace(/^-+|-+$/g, '');
        
        result.handle = handle;
        console.log('Tool executed: Generated handle from title', {
          productTitle: currentTitleForHandle,
          handle
        });
        break;
        
      case 'handle_prefix_suffix':
        // Add prefix/suffix to handle
        const handleParams = {};
        if (params) {
          params.split('|').forEach(param => {
            const [key, value] = param.split('=');
            if (key && value !== undefined) {
              handleParams[key.trim()] = value.trim();
            }
          });
        }
        
        // Get the product data from either the full product or the filtered data
        const productForHandlePS = productData.fullProduct || productData;
        
        const handlePrefix = handleParams.prefix || '';
        const handleSuffix = handleParams.suffix || '';
        
        // Get current handle from workflow updates, local edits, or original product
        let currentHandle = '';
        
        // 1. First check latestUpdates from previous templates in the workflow
        if (productData.latestUpdates && productData.latestUpdates.handle) {
          currentHandle = productData.latestUpdates.handle;
          console.log('📋 Using handle from workflow updates:', { currentHandle, source: 'latestUpdates' });
        }
        
        // 2. Then check local edits for handle
        if (!currentHandle && typeof window !== 'undefined' && window.localEdits && productForHandlePS.id) {
          const productId = productForHandlePS.id.toString();
          if (window.localEdits[productId] && window.localEdits[productId].handle !== undefined) {
            currentHandle = window.localEdits[productId].handle;
            console.log('📋 Using handle from local edits:', { currentHandle, productId, source: 'localEdits' });
          }
        }
        
        // 3. Fallback to original handle if no updates available
        if (!currentHandle) {
          currentHandle = productForHandlePS.handle || '';
          console.log('📋 Using original handle:', { currentHandle, source: 'original' });
        }
        
        if (!currentHandle) {
          // Get the current title from workflow updates, local edits, or original product
          let currentTitleForHandlePS = '';
          
          // 1. First check latestUpdates from previous templates in the workflow
          if (productData.latestUpdates && productData.latestUpdates.title) {
            currentTitleForHandlePS = productData.latestUpdates.title;
            console.log('📋 Using title from workflow updates for handle prefix/suffix:', { currentTitleForHandlePS, source: 'latestUpdates' });
          }
          
          // 2. Then check local edits from the UI
          if (!currentTitleForHandlePS && typeof window !== 'undefined' && window.localEdits && productForHandlePS.id) {
            const productId = productForHandlePS.id.toString();
            if (window.localEdits[productId] && window.localEdits[productId].title !== undefined) {
              currentTitleForHandlePS = window.localEdits[productId].title;
              console.log('📋 Using title from local edits for handle prefix/suffix:', { currentTitleForHandlePS, productId, source: 'localEdits' });
            }
          }
          
          // 3. Fallback to original title if no updates available
          if (!currentTitleForHandlePS) {
            currentTitleForHandlePS = productForHandlePS.title || '';
            console.log('📋 Using original title for handle prefix/suffix:', { currentTitleForHandlePS, source: 'original' });
          }
          
          // Make sure we have a valid product title
          if (!currentTitleForHandlePS) {
            return { error: 'Product title is required to generate handle' };
          }
          
          currentHandle = currentTitleForHandlePS
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_-]+/g, '-')
            .replace(/^-+|-+$/g, '');
        }
        
        result.handle = `${handlePrefix}${currentHandle}${handleSuffix}`;
        console.log('Tool executed: Added prefix/suffix to handle', {
          originalHandle: currentHandle,
          prefix: handlePrefix,
          suffix: handleSuffix,
          newHandle: result.handle
        });
        break;

      case 'empty_handle':
        // Set handle to empty string - when pushed to Shopify, it will auto-generate from title
        result.handle = '';
        console.log('Tool executed: Emptied handle (Shopify will auto-generate on push)', {
          productTitle: (productData.fullProduct || productData).title
        });
        break;
        
      case 'update_prices':
        // Update prices based on parameters - Enhanced with all manual tool options
        const priceParams = {};
        if (params) {
          params.split('|').forEach(param => {
            const [key, value] = param.split('=');
            if (key && value !== undefined) {
              priceParams[key.trim()] = value.trim();
            }
          });
        }
        
        // Get the product data from either the full product or the filtered data
        const productForPrices = productData.fullProduct || productData;
        
        const amount = parseFloat(priceParams.amount || '0');
        const changeType = priceParams.type || 'increase'; // Default to increase for backward compatibility
        const priceField = priceParams.field || 'price';
        
        // Create updates for each variant
        result.variants = {};
        
        // Get variants from the full product data
        const variantsForPrices = productForPrices.variants || [];
        
        // Create a simple array of price updates in the same format as the PricingTools component
        const priceUpdates = [];
        
        if (variantsForPrices.length > 0) {
          variantsForPrices.forEach(variant => {
            // Get current price from localEdits if available
            const originalPrice = parseFloat(variant.price || 0);
            const currentPrice = parseFloat(
              getCurrentVariantValue(productForPrices.id, variant.id, 'price', originalPrice)
            );
            
            // Get current compare price from localEdits if available
            const originalComparePrice = parseFloat(variant.compare_at_price || 0);
            const currentComparePrice = parseFloat(
              getCurrentVariantValue(productForPrices.id, variant.id, 'compare_at_price', originalComparePrice)
            );
            
            // Get the correct field value based on the priceField parameter
            let currentFieldValue = 0;
            if (priceField === 'compareAtPrice' || priceField === 'compare_at_price') {
              currentFieldValue = currentComparePrice;
            } else {
              currentFieldValue = currentPrice;
            }
            
            // Calculate new price based on change type - ALL OPTIONS FROM MANUAL TOOLS
            let newPrice = currentFieldValue;
            
            switch (changeType) {
              case 'increase':
                // Increase by percentage
                newPrice = currentFieldValue * (1 + amount / 100);
                break;
              case 'decrease':
                // Decrease by percentage
                newPrice = currentFieldValue * (1 - amount / 100);
                break;
              case 'increase_amount':
                // Increase by fixed amount
                newPrice = currentFieldValue + amount;
                break;
              case 'decrease_amount':
                // Decrease by fixed amount
                newPrice = currentFieldValue - amount;
                break;
              case 'set':
                // Set to specific amount
                newPrice = amount;
                break;
              case 'percentage': // Legacy support
                newPrice = currentFieldValue * (1 + amount / 100);
                break;
              case 'fixed': // Legacy support
                newPrice = currentFieldValue + amount;
                break;
              default:
                // Default to increase by percentage
                newPrice = currentFieldValue * (1 + amount / 100);
                break;
            }
            
            // Ensure price is not negative
            newPrice = Math.max(0, newPrice);
            
            // Format the price with 2 decimal places
            const formattedPrice = parseFloat(newPrice.toFixed(2));
            
            // Determine the correct field name for the result
            const resultFieldName = priceField === 'compareAtPrice' ? 'compare_at_price' : priceField;
            
            // Add to result for the template execution system
            result.variants[variant.id] = { [resultFieldName]: formattedPrice.toFixed(2) };
            
            // Add to price updates array with the correct field name for local edits
            priceUpdates.push({
              productId: productData.id,
              variantId: variant.id,
              field: priceField === 'compareAtPrice' ? 'compare_at_price' : priceField,
              value: formattedPrice.toFixed(2)
            });
          });
        }
        
        // SIMPLE DIRECT UPDATE: Apply price updates directly to window.localEdits
        // This uses the EXACT SAME approach as the manual price tools in handlePriceUpdate
        if (typeof window !== 'undefined' && window.localEdits && priceUpdates.length > 0) {
          try {
            priceUpdates.forEach(update => {
              const { productId, variantId, field, value } = update;
              
              // Initialize product in localEdits if needed
              if (!window.localEdits[productId]) {
                window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
              }
              
              // Initialize variant_updates if needed
              if (!window.localEdits[productId].variant_updates) {
                window.localEdits[productId].variant_updates = {};
              }
              
              // Initialize variant in localEdits if needed
              if (!window.localEdits[productId].variant_updates[variantId]) {
                window.localEdits[productId].variant_updates[variantId] = {};
              }
              
              // Update the specific field - EXACTLY as done in handlePriceUpdate
              const fieldName = field === 'compareAtPrice' ? 'compare_at_price' : field;
              window.localEdits[productId].variant_updates[variantId][fieldName] = 
                value !== null ? value.toString() : null;
              
              // Update timestamp to force re-render
              window.localEdits[productId]._timestamp = Date.now();
              
              console.log('Updated variant in localEdits:', {
                productId,
                variantId,
                field: fieldName,
                value,
                variant: window.localEdits[productId].variant_updates[variantId]
              });
            });
            
            // Set hasUnsavedChanges flag if it exists
            if (window.setHasUnsavedChanges && typeof window.setHasUnsavedChanges === 'function') {
              window.setHasUnsavedChanges(true);
            }
          } catch (error) {
            console.error('Error directly updating prices in window.localEdits:', error);
          }
        }
        
        console.log('Tool executed: Updated prices', {
          amount,
          changeType,
          priceField,
          variantCount: Object.keys(result.variants || {}).length,
          updates: priceUpdates
        });
        break;
        
      case 'set_minimum_price':
        // Set minimum price for all variants
        const minPrice = parseFloat(params || '0');
        
        const productForMinPrice = productData.fullProduct || productData;
        result.variants = {};
        const minPriceUpdates = [];
        
        if (productForMinPrice.variants) {
          productForMinPrice.variants.forEach(variant => {
            const originalPrice = parseFloat(variant.price || 0);
            const currentPrice = parseFloat(
              getCurrentVariantValue(productForMinPrice.id, variant.id, 'price', originalPrice)
            );
            
            if (currentPrice < minPrice) {
              result.variants[variant.id] = { price: minPrice.toFixed(2) };
              minPriceUpdates.push({
                productId: productData.id,
                variantId: variant.id,
                field: 'price',
                value: minPrice.toFixed(2)
              });
            }
          });
        }
        
        // Apply updates to localEdits
        if (typeof window !== 'undefined' && window.localEdits && minPriceUpdates.length > 0) {
          minPriceUpdates.forEach(update => {
            const { productId, variantId, field, value } = update;
            if (!window.localEdits[productId]) {
              window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
            }
            if (!window.localEdits[productId].variant_updates) {
              window.localEdits[productId].variant_updates = {};
            }
            if (!window.localEdits[productId].variant_updates[variantId]) {
              window.localEdits[productId].variant_updates[variantId] = {};
            }
            window.localEdits[productId].variant_updates[variantId][field] = value.toString();
            window.localEdits[productId]._timestamp = Date.now();
          });
          if (window.setHasUnsavedChanges) window.setHasUnsavedChanges(true);
        }
        
        console.log('Tool executed: Set minimum price', { minPrice, updatedVariants: minPriceUpdates.length });
        break;
        
      case 'set_profit_margin':
        // Calculate selling price based on cost and desired profit margin
        const margin = parseFloat(params || '0') / 100; // Convert percentage to decimal
        
        const productForMargin = productData.fullProduct || productData;
        result.variants = {};
        const marginUpdates = [];
        
        if (productForMargin.variants && margin > 0 && margin < 1) {
          productForMargin.variants.forEach(variant => {
            const originalPrice = parseFloat(variant.price || 0);
            const costPrice = parseFloat(
              getCurrentVariantValue(productForMargin.id, variant.id, 'price', originalPrice)
            );
            
            // Formula: selling price = cost / (1 - margin)
            const sellingPrice = costPrice / (1 - margin);
            const formattedPrice = parseFloat(sellingPrice.toFixed(2));
            
            result.variants[variant.id] = { price: formattedPrice.toFixed(2) };
            marginUpdates.push({
              productId: productData.id,
              variantId: variant.id,
              field: 'price',
              value: formattedPrice.toFixed(2)
            });
          });
        }
        
        // Apply updates to localEdits
        if (typeof window !== 'undefined' && window.localEdits && marginUpdates.length > 0) {
          marginUpdates.forEach(update => {
            const { productId, variantId, field, value } = update;
            if (!window.localEdits[productId]) {
              window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
            }
            if (!window.localEdits[productId].variant_updates) {
              window.localEdits[productId].variant_updates = {};
            }
            if (!window.localEdits[productId].variant_updates[variantId]) {
              window.localEdits[productId].variant_updates[variantId] = {};
            }
            window.localEdits[productId].variant_updates[variantId][field] = value.toString();
            window.localEdits[productId]._timestamp = Date.now();
          });
          if (window.setHasUnsavedChanges) window.setHasUnsavedChanges(true);
        }
        
        console.log('Tool executed: Set profit margin', { margin: margin * 100 + '%', updatedVariants: marginUpdates.length });
        break;
        
      case 'set_compare_price_discount':
        // Set compare-at price based on discount percentage
        const discountPercentTemplate = parseFloat(params || '0');
        
        const productForDiscount = productData.fullProduct || productData;
        result.variants = {};
        const discountUpdates = [];
        
        if (productForDiscount.variants) {
          productForDiscount.variants.forEach(variant => {
            const originalPrice = parseFloat(variant.price || 0);
            const currentPrice = parseFloat(
              getCurrentVariantValue(productForDiscount.id, variant.id, 'price', originalPrice)
            );
            
            if (discountPercentTemplate === 100 || discountPercentTemplate === 0) {
              // Remove compare at price
              result.variants[variant.id] = { compare_at_price: '0.00' };
              discountUpdates.push({
                productId: productData.id,
                variantId: variant.id,
                field: 'compare_at_price',
                value: '0.00'
              });
            } else if (discountPercentTemplate > 0 && discountPercentTemplate < 100) {
              // Calculate compare at price based on discount
              const factor = 100 / (100 - discountPercentTemplate);
              const comparePrice = parseFloat((currentPrice * factor).toFixed(2));
              
              result.variants[variant.id] = { compare_at_price: comparePrice.toFixed(2) };
              discountUpdates.push({
                productId: productData.id,
                variantId: variant.id,
                field: 'compare_at_price',
                value: comparePrice.toFixed(2)
              });
            }
          });
        }
        
        // Apply updates to localEdits
        if (typeof window !== 'undefined' && window.localEdits && discountUpdates.length > 0) {
          discountUpdates.forEach(update => {
            const { productId, variantId, field, value } = update;
            if (!window.localEdits[productId]) {
              window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
            }
            if (!window.localEdits[productId].variant_updates) {
              window.localEdits[productId].variant_updates = {};
            }
            if (!window.localEdits[productId].variant_updates[variantId]) {
              window.localEdits[productId].variant_updates[variantId] = {};
            }
            window.localEdits[productId].variant_updates[variantId][field] = value.toString();
            window.localEdits[productId]._timestamp = Date.now();
          });
          if (window.setHasUnsavedChanges) window.setHasUnsavedChanges(true);
        }
        
        console.log('Tool executed: Set compare price discount', { discountPercent: discountPercentTemplate + '%', updatedVariants: discountUpdates.length });
        break;
        
      case 'clear_compare_prices':
        // Clear all compare-at prices
        const productForClearTemplate = productData.fullProduct || productData;
        result.variants = {};
        const clearUpdates = [];
        
        if (productForClearTemplate.variants) {
          productForClearTemplate.variants.forEach(variant => {
            result.variants[variant.id] = { compare_at_price: '0.00' };
            clearUpdates.push({
              productId: productData.id,
              variantId: variant.id,
              field: 'compare_at_price',
              value: '0.00'
            });
          });
        }
        
        // Apply updates to localEdits
        if (typeof window !== 'undefined' && window.localEdits && clearUpdates.length > 0) {
          clearUpdates.forEach(update => {
            const { productId, variantId, field, value } = update;
            if (!window.localEdits[productId]) {
              window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
            }
            if (!window.localEdits[productId].variant_updates) {
              window.localEdits[productId].variant_updates = {};
            }
            if (!window.localEdits[productId].variant_updates[variantId]) {
              window.localEdits[productId].variant_updates[variantId] = {};
            }
            window.localEdits[productId].variant_updates[variantId][field] = value.toString();
            window.localEdits[productId]._timestamp = Date.now();
          });
          if (window.setHasUnsavedChanges) window.setHasUnsavedChanges(true);
        }
        
        console.log('Tool executed: Clear compare prices', { clearedVariants: clearUpdates.length });
        break;
        
      case 'sales_price_factor':
        // Apply sales price factor
        const factor = parseFloat(params || '1.0');
        
        // Get the product data from either the full product or the filtered data
        const productForFactor = productData.fullProduct || productData;
        
        // Create updates for each variant
        result.variants = {};
        const factorUpdates = [];
        
        // Get variants from the full product data
        const variantsForFactor = productForFactor.variants || [];
        
        if (variantsForFactor.length > 0) {
          variantsForFactor.forEach(variant => {
            // Get the current price from localEdits if available, otherwise use the original price
            const originalPrice = parseFloat(variant.price || 0);
            const currentPrice = parseFloat(
              getCurrentVariantValue(productForFactor.id, variant.id, 'price', originalPrice)
            );
            
            // We're not changing the regular price, just using it to calculate the compare-at price
            
            // Get the current compare-at price from localEdits if available, otherwise use the original
            const originalComparePrice = parseFloat(variant.compare_at_price || 0);
            const currentComparePrice = parseFloat(
              getCurrentVariantValue(productForFactor.id, variant.id, 'compare_at_price', originalComparePrice)
            );
            
            // Calculate the new compare-at price based on the factor
            // Set the compare price to be the factor of the current price
            let compareAtValue = null;
            const compareAtPrice = currentPrice * factor;
            compareAtValue = parseFloat(compareAtPrice.toFixed(2));
            
            // Add to result for the template execution system - only updating compare_at_price
            result.variants[variant.id] = { 
              compare_at_price: compareAtValue !== null ? compareAtValue.toFixed(2) : null
            };
            
            // Add compare price update to the updates array
            factorUpdates.push({
              productId: productForFactor.id,
              variantId: variant.id,
              field: 'compare_at_price',
              value: compareAtValue.toFixed(2)
            });
          });
        }
        
        // Apply updates directly to window.localEdits using the same approach as price updates
        if (typeof window !== 'undefined' && window.localEdits && factorUpdates.length > 0) {
          try {
            factorUpdates.forEach(update => {
              const { productId, variantId, field, value } = update;
              
              // Initialize product in localEdits if needed
              if (!window.localEdits[productId]) {
                window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
              }
              
              // Initialize variant_updates if needed
              if (!window.localEdits[productId].variant_updates) {
                window.localEdits[productId].variant_updates = {};
              }
              
              // Initialize variant in localEdits if needed
              if (!window.localEdits[productId].variant_updates[variantId]) {
                window.localEdits[productId].variant_updates[variantId] = {};
              }
              
              // Update the specific field - EXACTLY as done in handlePriceUpdate
              window.localEdits[productId].variant_updates[variantId][field] = 
                value !== null ? value.toString() : null;
              
              // Update timestamp to force re-render
              window.localEdits[productId]._timestamp = Date.now();
              
              console.log('Updated price with factor in localEdits:', {
                productId,
                variantId,
                field,
                value,
                variant: window.localEdits[productId].variant_updates[variantId]
              });
            });
            
            // Set hasUnsavedChanges flag if it exists
            if (window.setHasUnsavedChanges && typeof window.setHasUnsavedChanges === 'function') {
              window.setHasUnsavedChanges(true);
            }
          } catch (error) {
            console.error('Error directly updating prices with factor in window.localEdits:', error);
          }
        }
        
        console.log('Tool executed: Applied sales price factor to compare-at price', {
          factor,
          variantCount: Object.keys(result.variants || {}).length,
          updates: factorUpdates
        });
        break;
        
      case 'round_prices':
        // Round prices to nearest dollar plus cents value
        const cents = parseFloat(params || '0.99');
        
        // Get the product data from either the full product or the filtered data
        const productForRounding = productData.fullProduct || productData;
        
        // Create updates for each variant
        result.variants = {};
        
        // Get variants from the full product data
        const variantsForRounding = productForRounding.variants || [];
        
        // Create a simple array of price updates in the same format as the PricingTools component
        const roundingUpdates = [];
        
        if (variantsForRounding.length > 0) {
          variantsForRounding.forEach(variant => {
            // Get the current price from localEdits if available, otherwise use the original price
            const originalPrice = parseFloat(variant.price || 0);
            const currentPrice = parseFloat(
              getCurrentVariantValue(productForRounding.id, variant.id, 'price', originalPrice)
            );
            
            // Round the regular price
            const dollarPart = Math.floor(currentPrice);
            const newPrice = dollarPart + cents;
            const formattedPrice = parseFloat(newPrice.toFixed(2));
            
            // Get the current compare-at price from localEdits if available, otherwise use the original
            const originalComparePrice = parseFloat(variant.compare_at_price || 0);
            const currentComparePrice = parseFloat(
              getCurrentVariantValue(productForRounding.id, variant.id, 'compare_at_price', originalComparePrice)
            );
            
            // Round the compare-at price if it exists
            let formattedComparePrice = null;
            if (!isNaN(currentComparePrice) && currentComparePrice > 0) {
              const compareDollarPart = Math.floor(currentComparePrice);
              const newComparePrice = compareDollarPart + cents;
              formattedComparePrice = parseFloat(newComparePrice.toFixed(2));
            }
            
            // Add to result for the template execution system
            result.variants[variant.id] = { 
              price: formattedPrice.toFixed(2),
              compare_at_price: formattedComparePrice !== null ? formattedComparePrice.toFixed(2) : null
            };
            
            // Add regular price update to the updates array
            roundingUpdates.push({
              productId: productForRounding.id,
              variantId: variant.id,
              field: 'price',
              value: formattedPrice.toFixed(2)
            });
            
            // Add compare price update to the updates array if it exists
            if (formattedComparePrice !== null) {
              roundingUpdates.push({
                productId: productForRounding.id,
                variantId: variant.id,
                field: 'compare_at_price',
                value: formattedComparePrice.toFixed(2)
              });
            }
          });
        }
        
        // Apply updates directly to window.localEdits using the same approach as price updates
        if (typeof window !== 'undefined' && window.localEdits && roundingUpdates.length > 0) {
          try {
            roundingUpdates.forEach(update => {
              const { productId, variantId, field, value } = update;
              
              // Initialize product in localEdits if needed
              if (!window.localEdits[productId]) {
                window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
              }
              
              // Initialize variant_updates if needed
              if (!window.localEdits[productId].variant_updates) {
                window.localEdits[productId].variant_updates = {};
              }
              
              // Initialize variant in localEdits if needed
              if (!window.localEdits[productId].variant_updates[variantId]) {
                window.localEdits[productId].variant_updates[variantId] = {};
              }
              
              // Update the specific field - EXACTLY as done in handlePriceUpdate
              window.localEdits[productId].variant_updates[variantId][field] = 
                value !== null ? value.toString() : null;
              
              // Update timestamp to force re-render
              window.localEdits[productId]._timestamp = Date.now();
              
              console.log('Updated rounded price in localEdits:', {
                productId,
                variantId,
                field,
                value,
                variant: window.localEdits[productId].variant_updates[variantId]
              });
            });
            
            // Set hasUnsavedChanges flag if it exists
            if (window.setHasUnsavedChanges && typeof window.setHasUnsavedChanges === 'function') {
              window.setHasUnsavedChanges(true);
            }
          } catch (error) {
            console.error('Error directly updating rounded prices in window.localEdits:', error);
          }
        }
        
        console.log('Tool executed: Rounded prices', {
          cents,
          variantCount: Object.keys(result.variants || {}).length,
          updates: roundingUpdates
        });
        break;
        
      case 'compare_price_discount':
        // Set compare-at price discount
        const discountPercent = parseFloat(params || '20');
        
        // Get the product data from either the full product or the filtered data
        const productForCompare = productData.fullProduct || productData;
        
        // Create updates for each variant
        result.variants = {};
        const compareUpdates = [];
        
        // Get variants from the full product data
        const variantsForCompare = productForCompare.variants || [];
        
        if (variantsForCompare.length > 0) {
          variantsForCompare.forEach(variant => {
            // Get the current price from localEdits if available, otherwise use the original price
            const originalPrice = parseFloat(variant.price || 0);
            const currentPrice = parseFloat(
              getCurrentVariantValue(productForCompare.id, variant.id, 'price', originalPrice)
            );
            
            let compareAtValue = null;
            
            if (discountPercent < 100) {
              // Calculate compare-at price based on discount and the CURRENT price
              const compareAtPrice = currentPrice / (1 - discountPercent / 100);
              compareAtValue = compareAtPrice.toFixed(2);
            }
            
            // Add to result for the template execution system
            result.variants[variant.id] = { compare_at_price: compareAtValue };
            
            // Add to updates array for direct application
            compareUpdates.push({
              productId: productForCompare.id,
              variantId: variant.id,
              field: 'compare_at_price',
              value: compareAtValue
            });
          });
        }
        
        // Apply updates directly to window.localEdits using the same approach as price updates
        if (typeof window !== 'undefined' && window.localEdits && compareUpdates.length > 0) {
          try {
            compareUpdates.forEach(update => {
              const { productId, variantId, field, value } = update;
              
              // Initialize product in localEdits if needed
              if (!window.localEdits[productId]) {
                window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
              }
              
              // Initialize variant_updates if needed
              if (!window.localEdits[productId].variant_updates) {
                window.localEdits[productId].variant_updates = {};
              }
              
              // Initialize variant in localEdits if needed
              if (!window.localEdits[productId].variant_updates[variantId]) {
                window.localEdits[productId].variant_updates[variantId] = {};
              }
              
              // Update the specific field - EXACTLY as done in handlePriceUpdate
              window.localEdits[productId].variant_updates[variantId][field] = 
                value !== null ? value.toString() : null;
              
              // Update timestamp to force re-render
              window.localEdits[productId]._timestamp = Date.now();
              
              console.log('Updated compare price in localEdits:', {
                productId,
                variantId,
                field,
                value,
                variant: window.localEdits[productId].variant_updates[variantId]
              });
            });
            
            // Set hasUnsavedChanges flag if it exists
            if (window.setHasUnsavedChanges && typeof window.setHasUnsavedChanges === 'function') {
              window.setHasUnsavedChanges(true);
            }
          } catch (error) {
            console.error('Error directly updating compare prices in window.localEdits:', error);
          }
        }
        
        console.log('Tool executed: Set compare-at price discount', {
          discountPercent,
          variantCount: Object.keys(result.variants || {}).length,
          updates: compareUpdates
        });
        break;
        
      case 'clear_compare_prices':
        // Clear all compare-at prices
        // Get the product data from either the full product or the filtered data
        const productForClear = productData.fullProduct || productData;
        
        // Create updates for each variant
        result.variants = {};
        const clearCompareUpdates = [];
        
        // Get variants from the full product data
        const variantsForClear = productForClear.variants || [];
        
        if (variantsForClear.length > 0) {
          variantsForClear.forEach(variant => {
            // Add to result for the template execution system
            result.variants[variant.id] = { compare_at_price: 0 };
            
            // Add to updates array for direct application
            clearCompareUpdates.push({
              productId: productForClear.id,
              variantId: variant.id,
              field: 'compare_at_price',
              value: 0
            });
          });
        }
        
        // Apply updates directly to window.localEdits using the same approach as price updates
        if (typeof window !== 'undefined' && window.localEdits && clearCompareUpdates.length > 0) {
          try {
            clearCompareUpdates.forEach(update => {
              const { productId, variantId, field, value } = update;
              
              // Initialize product in localEdits if needed
              if (!window.localEdits[productId]) {
                window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
              }
              
              // Initialize variant_updates if needed
              if (!window.localEdits[productId].variant_updates) {
                window.localEdits[productId].variant_updates = {};
              }
              
              // Initialize variant in localEdits if needed
              if (!window.localEdits[productId].variant_updates[variantId]) {
                window.localEdits[productId].variant_updates[variantId] = {};
              }
              
              // Update the specific field - EXACTLY as done in handlePriceUpdate
              window.localEdits[productId].variant_updates[variantId][field] = null;
              
              // Update timestamp to force re-render
              window.localEdits[productId]._timestamp = Date.now();
              
              console.log('Cleared compare price in localEdits:', {
                productId,
                variantId,
                field,
                variant: window.localEdits[productId].variant_updates[variantId]
              });
            });
            
            // Set hasUnsavedChanges flag if it exists
            if (window.setHasUnsavedChanges && typeof window.setHasUnsavedChanges === 'function') {
              window.setHasUnsavedChanges(true);
            }
          } catch (error) {
            console.error('Error directly clearing compare prices in window.localEdits:', error);
          }
        }
        
        console.log('Tool executed: Cleared compare-at prices', {
          variantCount: Object.keys(result.variants || {}).length,
          updates: clearCompareUpdates
        });
        break;
        
      case 'set_inventory':
        // Set inventory quantity (with optional random range, tracking, and selling policy)
        const inventoryParams = {};
        if (params) {
          params.split('|').forEach(param => {
            const [key, value] = param.split('=');
            if (key && value !== undefined) {
              inventoryParams[key.trim()] = value.trim();
            }
          });
        }
        
        const useRange = inventoryParams.useRange === 'yes';
        const quantityMin = parseInt(inventoryParams.quantityMin || '0');
        const quantityMax = parseInt(inventoryParams.quantityMax || '0');
        const fixedQuantity = parseInt(inventoryParams.quantity || '0');
        const ignoreZero = inventoryParams.ignoreZero === 'yes';
        const trackQuantity = inventoryParams.trackQuantity || 'unchanged'; // 'unchanged' | 'yes' | 'no'
        const continueSelling = inventoryParams.continueSelling || 'unchanged'; // 'unchanged' | 'yes' | 'no'
        const chargeTaxes = inventoryParams.chargeTaxes || 'unchanged'; // 'unchanged' | 'yes' | 'no'
        
        // Get the product data from either the full product or the filtered data
        const productForInventory = productData.fullProduct || productData;
        
        // Create updates for each variant
        result.variants = {};
        const inventoryUpdates = [];
        
        // Get variants from the full product data
        const variantsForInventory = productForInventory.variants || [];
        
        if (variantsForInventory.length > 0) {
          variantsForInventory.forEach(variant => {
            // Get the current inventory from localEdits if available, otherwise use the original inventory
            const originalInventory = parseInt(variant.inventory_quantity || 0);
            const currentInventory = parseInt(
              getCurrentVariantValue(productForInventory.id, variant.id, 'inventory_quantity', originalInventory)
            );
            
            // Skip variants with 0 inventory if ignoreZero is true
            if (ignoreZero && currentInventory === 0) {
              return;
            }
            
            const variantUpdate = {};
            
            // Resolve quantity: fixed or random in range
            let resolvedQuantity;
            if (useRange) {
              const safeMin = Math.min(quantityMin, quantityMax);
              const safeMax = Math.max(quantityMin, quantityMax);
              resolvedQuantity = Math.floor(Math.random() * (safeMax - safeMin + 1)) + safeMin;
            } else {
              resolvedQuantity = fixedQuantity;
            }
            variantUpdate.inventory_quantity = resolvedQuantity;
            
            // Track quantity → sets inventory item "tracked" field
            if (trackQuantity !== 'unchanged') {
              variantUpdate.tracked = trackQuantity === 'yes';
            }
            
            // Continue selling → sets variant inventoryPolicy (CONTINUE / DENY)
            if (continueSelling !== 'unchanged') {
              variantUpdate.inventory_policy = continueSelling === 'yes' ? 'CONTINUE' : 'DENY';
            }
            
            // Charge taxes → sets variant taxable field
            if (chargeTaxes !== 'unchanged') {
              variantUpdate.taxable = chargeTaxes === 'yes';
            }
            
            // Add to result for the template execution system
            result.variants[variant.id] = variantUpdate;
            
            // Add inventory quantity update
            inventoryUpdates.push({
              productId: productData.id,
              variantId: variant.id,
              field: 'inventory_quantity',
              value: resolvedQuantity.toString()
            });
            
            // Add tracked update if changed
            if (trackQuantity !== 'unchanged') {
              inventoryUpdates.push({
                productId: productData.id,
                variantId: variant.id,
                field: 'tracked',
                value: (trackQuantity === 'yes').toString()
              });
            }
            
            // Add inventory_policy update if changed
            if (continueSelling !== 'unchanged') {
              inventoryUpdates.push({
                productId: productData.id,
                variantId: variant.id,
                field: 'inventory_policy',
                value: continueSelling === 'yes' ? 'CONTINUE' : 'DENY'
              });
            }
            
            // Add taxable update if changed
            if (chargeTaxes !== 'unchanged') {
              inventoryUpdates.push({
                productId: productData.id,
                variantId: variant.id,
                field: 'taxable',
                value: (chargeTaxes === 'yes').toString()
              });
            }
          });
        }
        
        // Apply updates directly to window.localEdits using the same approach as price updates
        if (typeof window !== 'undefined' && window.localEdits && inventoryUpdates.length > 0) {
          try {
            inventoryUpdates.forEach(update => {
              const { productId, variantId, field, value } = update;
              
              // Initialize product in localEdits if needed
              if (!window.localEdits[productId]) {
                window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
              }
              
              // Initialize variant_updates if needed
              if (!window.localEdits[productId].variant_updates) {
                window.localEdits[productId].variant_updates = {};
              }
              
              // Initialize variant in localEdits if needed
              if (!window.localEdits[productId].variant_updates[variantId]) {
                window.localEdits[productId].variant_updates[variantId] = {};
              }
              
              // Update the specific field
              window.localEdits[productId].variant_updates[variantId][field] = 
                value !== null ? value.toString() : null;
              
              // Update timestamp to force re-render
              window.localEdits[productId]._timestamp = Date.now();
              
              console.log('Updated inventory in localEdits:', {
                productId,
                variantId,
                field,
                value,
                variant: window.localEdits[productId].variant_updates[variantId]
              });
            });
            
            // Set hasUnsavedChanges flag if it exists
            if (window.setHasUnsavedChanges && typeof window.setHasUnsavedChanges === 'function') {
              window.setHasUnsavedChanges(true);
            }
          } catch (error) {
            console.error('Error directly updating inventory in window.localEdits:', error);
          }
        }
        
        console.log('Tool executed: Set inventory quantity', {
          useRange,
          quantityMin,
          quantityMax,
          fixedQuantity,
          ignoreZero,
          trackQuantity,
          continueSelling,
          chargeTaxes,
          variantCount: Object.keys(result.variants || {}).length
        });
        break;
        
      default:
        return { error: `Unknown tool: ${toolName}` };
    }
    
    return result;
  } catch (error) {
    console.error('Error executing tool template:', error);
    return { error: `Error executing tool: ${error.message}` };
  }
};

/**
 * Execute templates on products
 * @param {Array} templateIds - The IDs of templates to apply
 * @param {Array} products - The products to process
 * @param {Array} templates - The templates to apply (optional, will load by ID if not provided)
 * @param {Object} options - Execution options
 * @returns {Object} The execution results
 */
export const executeTemplates = async (templateIds, products, templates = [], options = {}) => {
  const {
    userId,
    shopId,
    onProgress,
    onComplete,
    getFieldValue
  } = options;
  
  console.log('%c[Template Execution Start]', 'color: #0099ff; font-weight: bold', {
    templateIds,
    templateCount: templateIds.length,
    productCount: products.length,
    productsData: products.map(p => ({ id: p.id, title: p.title })),
    userId,
    shopId,
    hasGetFieldValue: !!getFieldValue
  });
  
  // Initialize results
  const results = {};
  const latestUpdates = {};
  
  // Clear persistent sidebar results for a fresh start
  if (typeof window !== 'undefined') {
    window._persistentResults = [];
  }
  
  // Initialize progress tracking
  const totalOperations = templateIds.length * products.length;
  let completedOperations = 0;
  
  // Load templates if not provided
  const loadedTemplates = [...templates];
  
  // Load any missing templates by ID
  for (const templateId of templateIds) {
    if (!loadedTemplates.find(t => t.id === templateId)) {
      try {
        console.log('Loading template by ID:', templateId);
        const template = await getTemplateById(templateId);
        console.log('Successfully loaded template:', template?.name);
        console.log('Template prompt:', template?.prompt);
        
        if (template) {
          loadedTemplates.push(template);
        } else {
          console.error(`Template not found: ${templateId}`);
        }
      } catch (error) {
        console.error(`Error loading template ${templateId}:`, error);
      }
    }
  }
  
  console.log('Loaded templates:', loadedTemplates.map(t => ({ id: t.id, name: t.name })));
  
  if (loadedTemplates.length === 0) {
    console.error('No templates were loaded. Cannot proceed with execution.');
    return {};
  }
  
  // Check if all templates are tool templates
  const allToolTemplates = loadedTemplates.every(template => template.type === 'tool');
  
  // If all templates are tool templates, we can skip loading AI model settings
  let userModelSettings = null;
  if (userId && !allToolTemplates) {
    try {
      userModelSettings = await getUserModelSettings(userId);
      console.log('Loaded user model settings:', userModelSettings);
      
      // Set default base URLs if not provided
      if (!userModelSettings) {
        userModelSettings = {
          openai: { baseUrl: '/api/openai' },
          deepseek: { baseUrl: 'https://api.deepseek.com/v1' },
          gemini: { baseUrl: 'https://generativelanguage.googleapis.com/v1beta' },
          claude: { baseUrl: 'https://api.anthropic.com/v1' }
        };
        console.log('Using default model settings:', userModelSettings);
      } else {
        // Ensure all provider settings exist with default base URLs
        // This handles cases where settings were saved before a new provider was added
        if (!userModelSettings.openai) {
          userModelSettings.openai = { baseUrl: '/api/openai' };
        } else if (!userModelSettings.openai.baseUrl) {
          userModelSettings.openai.baseUrl = '/api/openai';
        }
        
        if (!userModelSettings.deepseek) {
          userModelSettings.deepseek = { baseUrl: 'https://api.deepseek.com/v1' };
        } else if (!userModelSettings.deepseek.baseUrl) {
          userModelSettings.deepseek.baseUrl = 'https://api.deepseek.com/v1';
        }
        
        if (!userModelSettings.gemini) {
          userModelSettings.gemini = { baseUrl: 'https://generativelanguage.googleapis.com/v1beta' };
        } else if (!userModelSettings.gemini.baseUrl) {
          userModelSettings.gemini.baseUrl = 'https://generativelanguage.googleapis.com/v1beta';
        }
        
        if (!userModelSettings.claude) {
          userModelSettings.claude = { baseUrl: 'https://api.anthropic.com/v1' };
        } else if (!userModelSettings.claude.baseUrl) {
          userModelSettings.claude.baseUrl = 'https://api.anthropic.com/v1';
        }
        
        console.log('Using configured model settings with defaults applied:', userModelSettings);
      }
    } catch (error) {
      console.error('Error loading user model settings:', error);
      // Create default settings
      userModelSettings = {
        openai: { baseUrl: '/api/openai' },
        deepseek: { baseUrl: 'https://api.deepseek.com/v1' },
        gemini: { baseUrl: 'https://generativelanguage.googleapis.com/v1beta' },
        claude: { baseUrl: 'https://api.anthropic.com/v1' }
      };
      console.log('Using fallback model settings due to error:', userModelSettings);
    }
  } else if (allToolTemplates) {
    console.log('All templates are tool templates, skipping AI model settings loading');
  }
  
  // Track template usage
  if (userId && shopId) {
    try {
      // Track usage for each template
      for (const templateId of templateIds) {
        const template = loadedTemplates.find(t => t.id === templateId);
        const templateType = template?.type || 'product';
        
        console.log('%c[Tracking Template Usage]', 'color: #cc9900; font-weight: bold', {
          templateId,
          templateName: template?.name,
          templateType
        });
        
        // Fix parameter order: userId, templateId, templateType, shopId
        await trackTemplateUsage(userId, templateId, templateType, shopId);
      }
    } catch (error) {
      console.error('Error tracking template usage:', error);
      // Continue execution even if tracking fails
    }
  }
  
  try {
    // Process each product
    for (let productIndex = 0; productIndex < products.length; productIndex++) {
      const product = products[productIndex];
      const productId = product.id || 'unknown';
      
      // Initialize product results
      results[productId] = {};
      latestUpdates[productId] = {};
      
      console.log('%c[Processing Product]', 'color: #6600cc; font-weight: bold', {
        productId,
        title: product.title,
        hasId: !!product.id,
        productData: JSON.stringify(product).substring(0, 200) + '...'
      });
      
      // Process each template
      for (const templateId of templateIds) {
        // Find the template
        const template = loadedTemplates.find(t => t.id === templateId);
        if (!template) {
          console.error(`Template not found: ${templateId}`);
          continue;
        }
        
        console.log('%c[Processing Template]', 'color: #009966; font-weight: bold', {
          templateId,
          templateName: template.name,
          inputFields: template.inputFields,
          outputFields: template.outputFields,
          inputMetafields: template.inputMetafields,
          outputMetafields: template.outputMetafields,
          modelId: template.modelId
        });
        
        try {
          // Prepare filtered product data based on template input fields
          const filteredProductData = prepareFilteredProductData(product, template, getFieldValue);
          
          console.log('Filtered product data:', filteredProductData);
          
          // Call progress callback before processing with detailed info
          if (onProgress) {
            const currentProgress = (completedOperations / totalOperations) * 100; // Convert to percentage
            const currentProductIndex = productIndex + 1;
            
            // For template indexing in messages, use workflow context if available
            // This ensures the detailed progress messages show correct template numbers in workflows
            let messageTemplateIndex, messageTotalTemplates;
            let isWorkflowExecution = false;

            if (options.workflowContext) {
              isWorkflowExecution = true;
              console.log('DEBUG: Received workflow context:', JSON.stringify(options.workflowContext));
              
              // Force-convert to numbers to avoid string concatenation issues
              messageTemplateIndex = Number(options.workflowContext.currentTemplateIndex);
              messageTotalTemplates = Number(options.workflowContext.totalTemplates);
              
              // Extra safeguard - ensure we have valid numbers
              if (isNaN(messageTemplateIndex) || messageTemplateIndex < 1) {
                console.warn('Invalid template index in workflow context, falling back to 1');
                messageTemplateIndex = 1;
              }
              
              console.log(`DEBUG: Using workflow context for messages: Template ${messageTemplateIndex}/${messageTotalTemplates}`);
            } else {
              messageTemplateIndex = templateIds.indexOf(templateId) + 1;
              messageTotalTemplates = templateIds.length;
              console.log(`DEBUG: Using local indexing for messages: Template ${messageTemplateIndex}/${messageTotalTemplates}`);
            }
            
            // For the progress object, use workflow context if available for consistency
            // This ensures both messages and object properties match for workflow executions
            let currentTemplateForProgress, totalTemplatesForProgress;
            
            if (isWorkflowExecution) {
              // For workflow executions, use the workflow context values for consistent reporting
              currentTemplateForProgress = messageTemplateIndex;
              totalTemplatesForProgress = messageTotalTemplates;
            } else {
              // For standalone executions, use local indexing
              currentTemplateForProgress = templateIds.indexOf(templateId) + 1;
              totalTemplatesForProgress = templateIds.length;
            }
            
            onProgress({
              progress: currentProgress,
              currentProduct: currentProductIndex,
              totalProducts: products.length,
              currentTemplate: currentTemplateForProgress, // Use workflow context values when available
              totalTemplates: totalTemplatesForProgress, // Use workflow context values when available
              productTitle: product.title || `Product ${product.id}`,
              templateName: template.name || `Template ${templateId}`,
              message: `Product ${currentProductIndex}/${products.length} - Template ${messageTemplateIndex}/${messageTotalTemplates}: ${template.name || templateId}`
            });
            
            // Debug the complete progress object
            console.log('Progress object sent:', {
              currentTemplate: currentTemplateForProgress,
              totalTemplates: totalTemplatesForProgress,
              message: `Product ${currentProductIndex}/${products.length} - Template ${messageTemplateIndex}/${messageTotalTemplates}: ${template.name || templateId}`
            });
          }
          
          let templateResult;
          
          // Check if this is a tool template
          if (template.type === 'tool') {
            console.log('Processing tool template:', template.name);
            
            // For tool templates, we'll execute the appropriate tool function directly
            // No need to call AI API for tool templates
            // Pass both the filtered data and the full product to ensure we have access to all needed fields
            // Also pass latestUpdates to get the most current data from previous templates in the workflow
            templateResult = await processToolTemplate(template, { 
              ...filteredProductData, 
              fullProduct: product,
              latestUpdates: latestUpdates[productId] || {}
            });
          } else if (template.type === 'image' && template.imageAnalysisMode === 'all') {
            // ===== ANALYZE ALL MODE: per-image AI calls (parity with TemplateTools) =====
            // In the UI, TemplateTools.processImageTemplateDirectly loops through each image
            // individually and makes a separate AI call for each. We replicate that here.
            console.log('%c[Image Template - Analyze All Mode]', 'color: #ff9900; font-weight: bold', {
              templateName: template.name,
              productTitle: product.title,
              productId
            });
            
            // Collect all images from the product
            let allImages = [];
            if (product.images?.edges) {
              allImages = product.images.edges.map(e => ({
                id: e.node?.id,
                url: e.node?.url || e.node?.src,
                src: e.node?.url || e.node?.src,
                alt: e.node?.altText || e.node?.alt,
                position: e.node?.position
              }));
            } else if (Array.isArray(product.images)) {
              allImages = product.images.map(img => ({
                id: img.id,
                url: img.src || img.url,
                src: img.src || img.url,
                alt: img.alt || img.altText,
                position: img.position
              }));
            }
            
            if (allImages.length === 0) {
              console.warn(`No images found for product ${productId}, skipping Analyze All`);
              templateResult = { error: 'No images found for product' };
            } else {
              console.log(`📸 ANALYZE ALL: Processing ${allImages.length} images for "${product.title}"`);
              const imageAnalysisResults = [];
              
              // Lazy-import services needed for per-image calls
              const { processWithAIProvider } = await import('./aiProviderService');
              const { getAIModelById } = await import('./aiModelService');
              const { getOpenAIApiKey } = await import('./apiKeyService');
              const { compressImageForAnalysis } = await import('./imageCompressionService');
              
              const model = await getAIModelById(template.modelId);
              if (!model) {
                console.error('AI model not found for image template:', template.modelId);
                templateResult = { error: 'AI model not found for image template' };
              } else {
                // Build user prompt from template
                let userPrompt = template.prompt || 'Analyze this product image in detail. Describe what you see.';
                // Replace placeholders with product data
                Object.entries(product).forEach(([key, value]) => {
                  if (typeof value === 'string') {
                    userPrompt = userPrompt.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
                  }
                });
                
                for (let j = 0; j < allImages.length; j++) {
                  const img = allImages[j];
                  const imageUrl = img.src || img.url;
                  
                  try {
                    console.log(`  Compressing image ${j + 1}/${allImages.length} before AI analysis...`);
                    const compressedImageUrl = await compressImageForAnalysis(imageUrl, 1024);
                    
                    // Build Vision API messages (identical to TemplateTools)
                    const analysisMessages = [
                      {
                        role: 'system',
                        content: `You are an AI assistant that analyzes product images for e-commerce.\n\nYOUR RESPONSE MUST BE A VALID JSON OBJECT. No markdown, no code blocks, no plain text. ONLY JSON.\n\nRequired JSON format:\n{\n  "description": "Your full detailed analysis goes here as a single plain text string",\n  "category": "one of: hero, detail, lifestyle, variant, scale, texture",\n  "suggestedAltText": "A short, unique, descriptive alt text for THIS image (max 125 chars)",\n  "bestPosition": "one of: top, middle, bottom, gallery"\n}\n\nRULES FOR "description":\n- Follow the user's analysis prompt and put ALL your analysis text into this field\n- Write as a single plain text string, be specific and detailed\n- Do NOT use nested JSON or markdown formatting inside this field\n\nRULES FOR "suggestedAltText":\n- Max 125 characters\n- Describe what is VISIBLE in the image: product type, color, material, angle, key features\n- Must be UNIQUE per image — never use generic text like "Product - Image 1"\n- Good example: "Brown faux leather tote bag with gold hardware, front view"\n- Bad example: "Large Capacity Women Handbag - Image 3"\n\nRULES FOR "category":\n- "hero": Primary full-product shot with clean background (max 1 per product)\n- "detail": Close-up, feature focus, or alternative angle\n- "lifestyle": Product in real-world use or styled setting\n- "variant": Same product in a DIFFERENT color or style\n- "scale": Shows product size relative to hand/body/ruler\n- "texture": Close-up of material/fabric/surface\n\nRULES FOR "bestPosition":\n- "top": Single best overview image\n- "middle": Detail shots, features — between text sections\n- "bottom": Variants, size charts, extra views\n- "gallery": Supplementary or similar angle images\n\nYou are analyzing one image from a SET. Most images are NOT "hero". Focus on what makes THIS image different.`
                      },
                      {
                        role: 'user',
                        content: [
                          { type: 'image_url', image_url: { url: compressedImageUrl, detail: 'high' } },
                          {
                            type: 'text', text: `Product: ${product.title || 'Unknown'} (Image ${j + 1} of ${allImages.length})\n\nAnalyze this image and respond as JSON. Put your analysis into the "description" field.\n\nAnalysis instructions: ${userPrompt}\n\nRESPOND WITH JSON ONLY. No markdown code blocks.`
                          }
                        ]
                      }
                    ];
                    
                    let jsonResponse;
                    
                    if (model.provider === 'openai') {
                      const apiKey = await getOpenAIApiKey(userId);
                      if (!apiKey) throw new Error('OpenAI API key is required');
                      
                      const response = await fetch('https://api.openai.com/v1/chat/completions', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
                        body: JSON.stringify({
                          model: model.internalName || 'gpt-4.1-mini',
                          messages: analysisMessages,
                          temperature: 0.5,
                          max_tokens: 1000,
                          response_format: { type: 'json_object' }
                        })
                      });
                      if (!response.ok) throw new Error(`OpenAI API failed: ${response.status}`);
                      jsonResponse = await response.json();
                    } else if (model.provider === 'gemini') {
                      const modelIdentifier = model.id || model.modelId || model.internalName;
                      const response = await processWithAIProvider({
                        provider: model.provider,
                        modelId: model.internalName || model.modelId,
                        prompt: userPrompt,
                        messages: analysisMessages,
                        images: [compressedImageUrl],
                        modelType: 'vision',
                        templateType: 'image',
                        userId
                      });
                      jsonResponse = {
                        choices: [{ message: { content: response } }]
                      };
                    } else if (model.provider === 'claude' || model.provider === 'anthropic') {
                      const response = await processWithAIProvider({
                        provider: model.provider,
                        modelId: model.internalName || model.modelId,
                        prompt: userPrompt,
                        messages: analysisMessages,
                        images: [compressedImageUrl],
                        modelType: 'vision',
                        templateType: 'image',
                        userId
                      });
                      jsonResponse = {
                        choices: [{ message: { content: response } }]
                      };
                    } else {
                      throw new Error(`Unsupported provider: ${model.provider}`);
                    }
                    
                    const rawContent = jsonResponse?.choices?.[0]?.message?.content || '';
                    let parsed = {};
                    
                    // Clean markdown blocks and parse JSON
                    try {
                      const cleaned = rawContent.replace(/```json\s*/gi, '').replace(/```\s*/g, '').trim();
                      parsed = JSON.parse(cleaned);
                    } catch {
                      try {
                        const jsonMatch = rawContent.match(/\{[\s\S]*\}/);
                        if (jsonMatch) {
                          parsed = JSON.parse(jsonMatch[0]);
                        }
                      } catch {
                        parsed = {};
                      }
                    }
                    
                    // Build clean description from parsed data
                    let cleanDescription = '';
                    if (parsed.description && typeof parsed.description === 'string' && !parsed.description.startsWith('{')) {
                      cleanDescription = parsed.description;
                    } else {
                      const skipKeys = ['category', 'suggestedAltText', 'suggestedalttext', 'bestPosition', 'bestposition', 'confidence', 'alt_text'];
                      const parts = [];
                      for (const [key, val] of Object.entries(parsed)) {
                        if (skipKeys.includes(key)) continue;
                        if (typeof val === 'string' && val.length > 0) {
                          parts.push(`${key}: ${val}`);
                        }
                      }
                      cleanDescription = parts.length > 0 ? parts.join('. ') : rawContent.replace(/```json|```/g, '').substring(0, 300).trim();
                    }
                    
                    imageAnalysisResults.push({
                      imageIndex: j + 1,
                      imageUrl: imageUrl,
                      imageId: img.id,
                      description: cleanDescription,
                      category: parsed.category || 'detail',
                      suggestedAltText: parsed.suggestedAltText || parsed.suggestedalttext || parsed.alt_text || parsed.suggested_alt_text || (cleanDescription ? cleanDescription.substring(0, 125) : `${product.title || 'Product'} - Image ${j + 1}`),
                      bestPosition: parsed.bestPosition || parsed.bestposition || 'middle',
                      confidence: parsed.confidence || 0.8
                    });
                    
                    console.log(`  ✓ Image ${j + 1}/${allImages.length} analyzed: [${parsed.category || 'detail'}]`);
                    
                  } catch (imgError) {
                    console.error(`  ✗ Error analyzing image ${j + 1}:`, imgError.message);
                    imageAnalysisResults.push({
                      imageIndex: j + 1,
                      imageUrl: imageUrl,
                      imageId: img.id,
                      description: 'Analysis failed',
                      category: 'detail',
                      suggestedAltText: `${product.title || 'Product'} - image ${j + 1} analysis failed`,
                      bestPosition: 'gallery',
                      confidence: 0,
                      error: imgError.message
                    });
                  }
                  
                  // Update progress within product
                  if (onProgress) {
                    const imageProgress = ((j + 1) / allImages.length) * 100;
                    onProgress({
                      progress: imageProgress,
                      currentProduct: productIndex + 1,
                      totalProducts: products.length,
                      message: `Analyzing image ${j + 1}/${allImages.length} of "${product.title}"...`
                    });
                  }
                }
                
                // Store _imageAnalysis on product for downstream templates (image placeholders, etc.)
                product._imageAnalysis = imageAnalysisResults;
                console.log(`Stored _imageAnalysis on product for "${product.title}": ${imageAnalysisResults.length} results`);
                
                // Format combined analysis for description (identical to TemplateTools)
                let analysisSummary = `<h3>Image Analysis Results (${imageAnalysisResults.length} images)</h3>\n`;
                imageAnalysisResults.forEach(analysis => {
                  analysisSummary += `<p><strong>Image ${analysis.imageIndex} [${analysis.category}]:</strong> ${analysis.description}</p>\n`;
                  analysisSummary += `<p><em>Alt text:</em> ${analysis.suggestedAltText}</p>\n`;
                  analysisSummary += `<p><em>Best position:</em> ${analysis.bestPosition}</p>\n<hr>\n`;
                });
                
                // Build templateResult to match processTemplateResult expectations
                const insertMode = template.outputModes?.body_html || 'add';
                templateResult = {};
                
                // Only write to description if not sidebar-only
                if (!template.sidebarOnly) {
                  templateResult.body_html = analysisSummary;
                  templateResult.description = analysisSummary;
                  templateResult._insertModes = { body_html: insertMode, description: insertMode };
                  console.log(`✅ Wrote analysis summary to description for "${product.title}" (mode: ${insertMode})`);
                } else {
                  console.log(`ℹ️ Template is sidebar-only, not writing to description`);
                }
                
                // Store analysis data for downstream templates
                templateResult._imageAnalysis = imageAnalysisResults;
              }
            }
            // ===== END ANALYZE ALL MODE =====
          } else {
            // Process the template with the selected AI model
            console.log('Processing template with AI model:', template.modelId || 'default');
            
            // Inject product image count for image placeholder support
            if (template.enableImagePlaceholders) {
              let imageCount = 0;
              if (product.images?.edges) {
                imageCount = product.images.edges.length;
              } else if (Array.isArray(product.images)) {
                imageCount = product.images.length;
              }
              template._productImageCount = imageCount;
              console.log('%c[Image Placeholder] Product image count injected:', 'color: #e67e22; font-weight: bold', imageCount);
              
              // Inject image analysis data if available (from Analyze All Images mode)
              if (product._imageAnalysis && Array.isArray(product._imageAnalysis)) {
                template._imageAnalyses = product._imageAnalysis;
                // Switch to smart mode when analysis data is available
                template.imagePlaceholderMode = 'smart';
                console.log('%c[Image Placeholder] Smart analysis data injected:', 'color: #27ae60; font-weight: bold', product._imageAnalysis.length, 'images analyzed');
              }
            }
            
            // Inject name pool forbidden names if configured
            if (template.namePoolId) {
              try {
                const { getNamePoolById } = await import('./namePoolService');
                const namePool = await getNamePoolById(template.namePoolId);
                let forbiddenNames = [];
                if (namePool && Array.isArray(namePool.usedNames) && namePool.usedNames.length > 0) {
                  forbiddenNames = [...namePool.usedNames];
                }
                
                // Also include pending (not yet pushed) names from this batch
                // to prevent duplicates within the same execution run
                Object.values(latestUpdates).forEach(updates => {
                  if (updates._pendingNamePoolEntries) {
                    updates._pendingNamePoolEntries
                      .filter(e => e.poolId === template.namePoolId)
                      .forEach(e => {
                        if (!forbiddenNames.includes(e.name)) {
                          forbiddenNames.push(e.name);
                        }
                      });
                  }
                });
                
                if (forbiddenNames.length > 0) {
                  template._forbiddenNames = forbiddenNames;
                  console.log('%c[Name Pool] Forbidden names injected (pool + pending):', 'color: #e67e22; font-weight: bold', forbiddenNames.length);
                }
              } catch (err) {
                console.error('Failed to load name pool for execution', err);
              }
            }
            
            // For image templates with 'featured' mode, only send the featured image
            let imagesToSend;
            if (template.type === 'image' && (template.imageAnalysisMode === 'featured' || template.useFeaturedImage)) {
              // Featured mode: send only the first/featured image
              const allImgs = product.images?.edges 
                ? product.images.edges.map(e => e.node?.url || e.node?.src || e.node) 
                : (Array.isArray(product.images) ? product.images.map(img => img.url || img.src || img) : []);
              imagesToSend = allImgs.length > 0 ? [allImgs[0]] : [];
              console.log('%c[Image Template - Featured Mode] Sending only featured image', 'color: #ff9900; font-weight: bold');
            } else {
              imagesToSend = product.images?.edges 
                ? product.images.edges.map(e => e.node?.url || e.node?.src || e.node) 
                : (Array.isArray(product.images) ? product.images.map(img => img.url || img.src || img) : []);
            }
            
            templateResult = await processTemplateWithOpenAI(
              template,
              filteredProductData,
              null, // No longer passing apiKey directly
              { 
                userId, 
                userModelSettings,
                modelId: template.modelId, // Pass the modelId from the template
                images: imagesToSend
              }
            );
            
            // DEFERRED: Store generated name as pending for the Name Pool
            // Names are only committed to the pool on successful Push to Shopify.
            // This prevents "burning" names when the user re-generates or never pushes.
            if (template.namePoolId && templateResult && !templateResult.error && templateResult.generated_name) {
              // Store in latestUpdates so it travels with the product data to push handler
              if (!latestUpdates[productId]) {
                latestUpdates[productId] = {};
              }
              if (!latestUpdates[productId]._pendingNamePoolEntries) {
                latestUpdates[productId]._pendingNamePoolEntries = [];
              }
              // Remove any previous pending entry for same pool (user re-generated)
              latestUpdates[productId]._pendingNamePoolEntries = 
                latestUpdates[productId]._pendingNamePoolEntries.filter(e => e.poolId !== template.namePoolId);
              // Add the new pending entry
              latestUpdates[productId]._pendingNamePoolEntries.push({
                poolId: template.namePoolId,
                name: templateResult.generated_name
              });
              console.log('%c[Name Pool] DEFERRED – name stored as pending (will commit on Push to Shopify):', 'color: #e67e22; font-weight: bold', templateResult.generated_name);
              
              // Also store in window.localEdits so it survives until push
              try {
                if (typeof window !== 'undefined' && window.localEdits) {
                  // Create product entry if it doesn't exist yet
                  if (!window.localEdits[productId]) {
                    window.localEdits[productId] = {};
                  }
                  if (!window.localEdits[productId]._pendingNamePoolEntries) {
                    window.localEdits[productId]._pendingNamePoolEntries = [];
                  }
                  window.localEdits[productId]._pendingNamePoolEntries = 
                    window.localEdits[productId]._pendingNamePoolEntries.filter(e => e.poolId !== template.namePoolId);
                  window.localEdits[productId]._pendingNamePoolEntries.push({
                    poolId: template.namePoolId,
                    name: templateResult.generated_name
                  });
                  console.log('%c[Name Pool] Stored pending entry in window.localEdits:', 'color: #3498db; font-weight: bold', {
                    productId,
                    poolId: template.namePoolId,
                    name: templateResult.generated_name,
                    totalPending: window.localEdits[productId]._pendingNamePoolEntries.length
                  });
                }
              } catch (err) {
                console.error('Failed to store pending name pool entry in localEdits', err);
              }
            }
          }
          
          console.log('Template result:', templateResult);
          
          // Check if there was an error in the template result
          if (templateResult.error) {
            console.error(`Error in template result for ${template.name}:`, templateResult.error);
            
            // Store the error in the results
            results[productId][templateId] = {
              error: templateResult.error,
              metafields: {}
            };
          } else {
            // PRE-PROCESSING: Normalize tags if AI returned as array instead of comma-separated string
            // BulkEditProduct/TemplateTools expects tags as a comma-separated string
            if (templateResult.tags !== undefined && Array.isArray(templateResult.tags)) {
              console.log(`🏷️ Normalizing tags array to comma-separated string:`, templateResult.tags);
              templateResult.tags = templateResult.tags.join(', ');
            }
            
            let processedResult;
            
            // For image analyze-all templates, skip processTemplateResult() entirely
            // processTemplateResult() only extracts fields in template.outputFields, which strips
            // _imageAnalysis, _insertModes and other critical data that TemplateTools needs.
            // This matches how TemplateTools.processImageTemplateDirectly works in manual mode.
            if (template.type === 'image' && template.imageAnalysisMode === 'all' && templateResult._imageAnalysis) {
              console.log('%c[Image Analyze All: Bypassing processTemplateResult]', 'color: #ff9900; font-weight: bold', {
                imageCount: templateResult._imageAnalysis.length,
                hasInsertModes: !!templateResult._insertModes,
                hasBodyHtml: !!templateResult.body_html
              });
              processedResult = { ...templateResult };
            } else {
              // Process the template result through standard pipeline
              processedResult = processTemplateResult(templateResult, product, template);
            }
            
            console.log('Processed template result:', processedResult);
            
            // Special handling for tool templates with variants
            if (template.type === 'tool' && templateResult.variants && Object.keys(templateResult.variants).length > 0) {
              console.log('%c[Tool Template Variants Detected]', 'color: #ff6600; font-weight: bold', {
                productId,
                templateId,
                variantCount: Object.keys(templateResult.variants).length
              });
              
              // Map variants to variant_skus for consistent processing
              processedResult.variant_skus = templateResult.variants;
            }
            
            // Add top-level fields to latestUpdates FIRST (before storing in results)
            const topLevelFields = ['title', 'body_html', 'description', 'vendor', 'product_type', 'tags', 'handle'];
            for (const field of topLevelFields) {
              if (processedResult[field] !== undefined) {
                if (!latestUpdates[productId]) {
                  latestUpdates[productId] = {};
                }
                latestUpdates[productId][field] = processedResult[field];
              }
            }
            
            // IMMEDIATE UI UPDATES: Apply changes to UI immediately after each template
            // This ensures that all templates show their results immediately, not just at the end
            try {
              if (typeof window !== 'undefined' && window.localEdits && window.localEdits[productId]) {
                // Update top-level fields in localEdits for immediate UI display
                for (const field of topLevelFields) {
                  if (processedResult[field] !== undefined) {
                    window.localEdits[productId][field] = processedResult[field];
                    window.localEdits[productId]._timestamp = Date.now();
                    console.log(`🔄 IMMEDIATE UI UPDATE: Applied ${field} to product ${productId}:`, processedResult[field]);
                    
                    // Special handling for body_html -> descriptionHtml mapping
                    // DO NOT map to descriptionHtml here - let TemplateTools handle it with correct insert mode
                    if (field === 'body_html') {
                      console.log(`⏭️ SKIPPING descriptionHtml mapping in immediate UI update - will be handled by TemplateTools with correct insert mode`);
                    }
                  }
                }
                
                // Trigger UI re-render by dispatching a custom event
                if (typeof window.dispatchEvent === 'function') {
                  window.dispatchEvent(new CustomEvent('localEditsUpdated', {
                    detail: { productId, updatedFields: Object.keys(processedResult) }
                  }));
                }
                
                // For analyze-all image templates, also store _imageAnalysis in localEdits
                // This enables downstream templates and UI to access analysis data
                if (processedResult._imageAnalysis && Array.isArray(processedResult._imageAnalysis)) {
                  window.localEdits[productId]._imageAnalysis = processedResult._imageAnalysis;
                  console.log(`📸 Stored _imageAnalysis in window.localEdits for "${product.title}": ${processedResult._imageAnalysis.length} images`);
                }
              }
            } catch (uiUpdateError) {
              console.error('Error applying immediate UI updates:', uiUpdateError);
              // Don't fail the entire template execution if UI update fails
            }
            
            // BUILD PERSISTENT SIDEBAR RESULTS for PersistentResultsSidebar visibility
            // Only build sidebar entries when the template has sidebarOnly enabled
            // Otherwise results are written directly to product fields (description, etc.)
            try {
              if (typeof window !== 'undefined' && template.sidebarOnly) {
                if (!window._persistentResults) {
                  window._persistentResults = [];
                }
                
                // Collect the fields that were actually updated
                const updatedFields = {};
                const topLevelFields = ['title', 'body_html', 'description', 'vendor', 'product_type', 'tags', 'handle'];
                for (const field of topLevelFields) {
                  if (processedResult[field] !== undefined) {
                    updatedFields[field] = processedResult[field];
                  }
                }
                
                // Add metafields if present
                if (processedResult.metafields) {
                  Object.entries(processedResult.metafields).forEach(([key, val]) => {
                    updatedFields[`metafield:${key}`] = typeof val === 'object' ? val.value || JSON.stringify(val) : val;
                  });
                }
                
                // Add image analysis results if present
                if (processedResult._imageAnalysis && Array.isArray(processedResult._imageAnalysis)) {
                  processedResult._imageAnalysis.forEach((analysis, idx) => {
                    updatedFields[`image_${idx + 1}_alt_text`] = analysis.suggestedAltText || '';
                    updatedFields[`image_${idx + 1}_category`] = analysis.category || '';
                    updatedFields[`image_${idx + 1}_description`] = analysis.description || '';
                  });
                }
                
                // Build an entry ID that combines product + template
                const entryId = `${productId}_${templateId}`;
                
                // Check if we already have an entry for this combination
                const existingIdx = window._persistentResults.findIndex(r => r.id === entryId);
                const sidebarEntry = {
                  id: entryId,
                  productName: product.title || `Product ${productId}`,
                  templateName: template.name || templateId,
                  status: 'success',
                  fields: updatedFields,
                  imageUrl: (product.images?.edges?.[0]?.node?.url) || 
                            (product.images?.edges?.[0]?.node?.src) ||
                            (Array.isArray(product.images) && product.images[0]?.src) ||
                            null
                };
                
                if (existingIdx >= 0) {
                  window._persistentResults[existingIdx] = sidebarEntry;
                } else {
                  window._persistentResults.push(sidebarEntry);
                }
                
                console.log(`📊 Built persistent sidebar result for "${template.name}" on "${product.title}": ${Object.keys(updatedFields).length} fields`);
                
                // Dispatch event for sidebar to pick up
                if (typeof window.dispatchEvent === 'function') {
                  window.dispatchEvent(new CustomEvent('persistentResultsUpdated', {
                    detail: { results: window._persistentResults }
                  }));
                }
              }
            } catch (sidebarError) {
              console.error('Error building persistent sidebar results:', sidebarError);
            }
            // Store the processed result AFTER body_html has been removed
            // This ensures body_html is NOT passed to TemplateTools.processTemplateResults
            results[productId][templateId] = processedResult;
            console.log(`📦 Stored result for template ${templateId} (body_html removed if present)`);
            
            // Store template execution metadata for potential refunds
            if (!latestUpdates[productId]._templateExecutions) {
              latestUpdates[productId]._templateExecutions = [];
            }
            
            // Handle variant SKUs if present
            if (processedResult.variant_skus && Object.keys(processedResult.variant_skus).length > 0) {
              console.log('%c[Processing Variant SKUs for Updates]', 'color: #6600cc; font-weight: bold', {
                productId,
                variantCount: Object.keys(processedResult.variant_skus).length
              });
              
              // Initialize variants object if needed
              if (!latestUpdates[productId].variants) {
                latestUpdates[productId].variants = {};
              }
              
              // Apply SKU updates to each variant
              Object.entries(processedResult.variant_skus).forEach(([variantId, variantData]) => {
                // Initialize this variant if needed
                if (!latestUpdates[productId].variants[variantId]) {
                  latestUpdates[productId].variants[variantId] = {};
                }
                
                // Apply the SKU update
                if (variantData.sku) {
                  latestUpdates[productId].variants[variantId].sku = variantData.sku;
                  console.log(`Applied SKU ${variantData.sku} to variant ${variantId}`);
                }
                
                // Apply the price update
                if (variantData.price) {
                  latestUpdates[productId].variants[variantId].price = variantData.price;
                  console.log(`Applied price ${variantData.price} to variant ${variantId}`);
                }
                
                // Apply the compare at price update
                if (variantData.compare_at_price) {
                  latestUpdates[productId].variants[variantId].compare_at_price = variantData.compare_at_price;
                  console.log(`Applied compare at price ${variantData.compare_at_price} to variant ${variantId}`);
                }
              });
              
              // Also update window.localEdits for immediate UI updates
              try {
                if (typeof window !== 'undefined' && window.localEdits && window.localEdits[productId]) {
                  // Initialize variants object if needed
                  if (!window.localEdits[productId].variants) {
                    window.localEdits[productId].variants = {};
                  }
                  
                  // Apply SKU updates to each variant in localEdits
                  Object.entries(processedResult.variant_skus).forEach(([variantId, variantData]) => {
                    // Initialize this variant if needed
                    if (!window.localEdits[productId].variants[variantId]) {
                      window.localEdits[productId].variants[variantId] = {};
                    }
                    
                    // Apply the SKU update
                    if (variantData.sku) {
                      window.localEdits[productId].variants[variantId].sku = variantData.sku;
                      window.localEdits[productId].variants[variantId]._timestamp = Date.now();
                      console.log(`Applied SKU ${variantData.sku} to variant ${variantId} in window.localEdits`);
                    }
                    
                    // Apply the price update
                    if (variantData.price) {
                      window.localEdits[productId].variants[variantId].price = variantData.price;
                      window.localEdits[productId].variants[variantId]._timestamp = Date.now();
                      console.log(`Applied price ${variantData.price} to variant ${variantId} in window.localEdits`);
                    }
                    
                    // Apply the compare at price update
                    if (variantData.compare_at_price) {
                      window.localEdits[productId].variants[variantId].compareAtPrice = variantData.compare_at_price;
                      window.localEdits[productId].variants[variantId]._timestamp = Date.now();
                      console.log(`Applied compare at price ${variantData.compare_at_price} to variant ${variantId} in window.localEdits`);
                    }
                  });
                }
              } catch (error) {
                console.error('Error updating window.localEdits for variant SKUs:', error);
              }
            }
            
            // Add metafields to latestUpdates
            if (processedResult.metafields) {
              // Initialize metafields in latestUpdates if needed
              if (!latestUpdates[productId].metafields) {
                latestUpdates[productId].metafields = {};
              }
              if (!latestUpdates[productId].metafield_updates) {
                latestUpdates[productId].metafield_updates = {};
              }
              if (!latestUpdates[productId].metafields_array) {
                latestUpdates[productId].metafields_array = [];
              }
              
              // Process each metafield
              Object.entries(processedResult.metafields).forEach(([metafieldKey, value]) => {
                // Skip if the value is undefined or null
                if (value === undefined || value === null) return;
                
                // Parse namespace and key from metafieldKey (format: namespace.key)
                const [namespace, key] = metafieldKey.split('.');
                if (!namespace || !key) {
                  console.error(`Invalid metafield key format: ${metafieldKey}`);
                  return;
                }
                
                console.log(`%c[Adding metafield ${metafieldKey}]`, 'color: #4CAF50; font-weight: bold', {
                  value,
                  valueType: typeof value,
                  valueLength: typeof value === 'string' ? value.length : null
                });
                
                // Add to metafields object
                if (!latestUpdates[productId].metafields[metafieldKey]) {
                  latestUpdates[productId].metafields[metafieldKey] = {
                    value: value,
                    _timestamp: Date.now()
                  };
                } else {
                  latestUpdates[productId].metafields[metafieldKey].value = value;
                  latestUpdates[productId].metafields[metafieldKey]._timestamp = Date.now();
                }
                
                // Add to metafield_updates
                latestUpdates[productId].metafield_updates[metafieldKey] = value;
                
                // Check if the metafield already exists in the array
                const existingIndex = latestUpdates[productId].metafields_array.findIndex(
                  m => m.namespace === namespace && m.key === key
                );
                
                if (existingIndex >= 0) {
                  // Update existing metafield
                  latestUpdates[productId].metafields_array[existingIndex].value = value;
                } else {
                  // Add new metafield
                  latestUpdates[productId].metafields_array.push({
                    namespace,
                    key,
                    value,
                    type: 'single_line_text_field' // Default type
                  });
                }
                
                // Update window.localEdits directly for immediate UI updates
                try {
                  if (window.localEdits && window.localEdits[productId]) {
                    // Update metafields object
                    if (!window.localEdits[productId].metafields) {
                      window.localEdits[productId].metafields = {};
                    }
                    window.localEdits[productId].metafields[metafieldKey] = {
                      value: value,
                      _timestamp: Date.now()
                    };
                    
                    // Update metafield_updates
                    if (!window.localEdits[productId].metafield_updates) {
                      window.localEdits[productId].metafield_updates = {};
                    }
                    window.localEdits[productId].metafield_updates[metafieldKey] = value;
                    
                    // Update metafields_array
                    if (!window.localEdits[productId].metafields_array) {
                      window.localEdits[productId].metafields_array = [];
                    }
                    
                    const localExistingIndex = window.localEdits[productId].metafields_array.findIndex(
                      m => m.namespace === namespace && m.key === key
                    );
                    
                    if (localExistingIndex >= 0) {
                      window.localEdits[productId].metafields_array[localExistingIndex].value = value;
                    } else {
                      window.localEdits[productId].metafields_array.push({
                        namespace,
                        key,
                        value,
                        type: 'single_line_text_field' // Default type
                      });
                    }
                    
                    console.log(`%c[Updated window.localEdits for ${metafieldKey}]`, 'color: #2196F3; font-weight: bold', {
                      productId,
                      metafieldKey,
                      value
                    });
                  }
                } catch (error) {
                  console.error('Error updating window.localEdits:', error);
                }
              });
            }
            
            console.log('%c[Template Result Processed]', 'color: #009966; font-weight: bold', {
              templateId,
              templateName: template.name,
              result: processedResult,
              metafieldCount: processedResult.metafields ? Object.keys(processedResult.metafields).length : 0
            });
            
            // Deduct credits for successful template execution
            if (userId) {
              try {
                // Calculate output fields count
                const outputFieldsCount = (template.outputFields || []).length;
                const outputMetafieldsCount = (template.outputMetafields || []).length;
                const totalOutputs = outputFieldsCount + outputMetafieldsCount;
                
                // For tool templates, we don't deduct any credits
                if (template.type === 'tool') {
                  // Create execution metadata with zero credit cost
                  const executionMetadata = {
                    templateId: template.id,
                    templateName: template.name,
                    productId,
                    productTitle: product.title,
                    outputFields: template.outputFields,
                    outputMetafields: template.outputMetafields,
                    totalOutputs,
                    effectiveTotalOutputs: totalOutputs,
                    creditCostPerOutput: 0,
                    totalCreditCost: 0,
                    shopId,
                    executionId: `${template.id}_${productId}_${Date.now()}`
                  };
                  
                  // Store the execution metadata in the results
                  results[productId][templateId].executionMetadata = executionMetadata;
                  
                  // Also store in latestUpdates for tracking
                  if (!latestUpdates[productId]._templateExecutions) {
                    latestUpdates[productId]._templateExecutions = [];
                  }
                  latestUpdates[productId]._templateExecutions.push(executionMetadata);
                  
                  // Track template usage for analytics only (no credit deduction)
                  await trackTemplateUsage(userId, template.id, 'tool', shopId);
                  
                  console.log(`%c[Tool template executed with no credits]`, 'color: #4CAF50; font-weight: bold', {
                    templateId,
                    templateName: template.name,
                    productId
                  });
                } else {
                  // For AI templates, calculate and deduct credits as usual
                  // Ensure at least 1 credit is charged for AI templates
                  const effectiveTotalOutputs = Math.max(totalOutputs, 1);
                  
                  // Get the credit cost per output for this model
                  const creditCostPerOutput = template.creditCostPerOutput || 1;
                  const totalCreditCost = effectiveTotalOutputs * creditCostPerOutput;
                  
                  // Create execution metadata
                  const executionMetadata = {
                    templateId: template.id,
                    templateName: template.name,
                    productId,
                    productTitle: product.title,
                    outputFields: template.outputFields,
                    outputMetafields: template.outputMetafields,
                    totalOutputs,
                    effectiveTotalOutputs,
                    creditCostPerOutput,
                    totalCreditCost,
                    shopId,
                    executionId: `${template.id}_${productId}_${Date.now()}`
                  };
                  
                  // Store the execution metadata in the results
                  results[productId][templateId].executionMetadata = executionMetadata;
                  
                  // Also store in latestUpdates for refunds during push to Shopify
                  if (!latestUpdates[productId]._templateExecutions) {
                    latestUpdates[productId]._templateExecutions = [];
                  }
                  latestUpdates[productId]._templateExecutions.push(executionMetadata);
                  
                  // Deduct credits for AI templates
                  await deductCreditsForAIModel(userId, totalCreditCost, executionMetadata);
                  
                  // Track template usage for analytics and reporting
                  await trackTemplateUsage(userId, template.id, template.type || 'product', shopId);
                  
                  console.log(`%c[Credits deducted for ${template.name}]`, 'color: #FF9800; font-weight: bold', {
                    templateId,
                    templateName: template.name,
                    productId,
                    totalCreditCost,
                    totalOutputs,
                    executionId: executionMetadata.executionId
                  });
                }
              } catch (error) {
                console.error(`Error deducting credits for ${template.name}:`, error);
                // We don't need to refund here since the deduction failed
                // Just log the error and continue
              }
            }
          }
        } catch (error) {
          console.error(`Error processing template ${templateId} for product ${productId}:`, error);
          
          // Store the error
          results[productId][templateId] = {
            error: error.message || 'Unknown error'
          };
        }
        
        // Update progress
        completedOperations++;
        if (onProgress && typeof onProgress === 'function') {
          try {
            const progressPercentage = (completedOperations / totalOperations) * 100; // Convert to percentage
            onProgress(progressPercentage);
          } catch (progressError) {
            console.error('Error in progress callback:', progressError);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error in template execution:', error);
  }
  
  console.log('%c[Template Execution Complete]', 'color: #0099ff; font-weight: bold', {
    totalOperations,
    completedOperations,
    success: completedOperations === totalOperations,
    results: JSON.stringify(results).substring(0, 500) + '...'
  });
  
  // Call onComplete callback if provided
  if (onComplete) {
    onComplete({
      results,
      latestUpdates,
      totalOperations,
      completedOperations,
      success: completedOperations === totalOperations
    });
  }
  
  return results;
};
